import { Button, Space, Tag, Typography } from "@arco-design/web-react";

import { canUsePermission } from "../../../lib/auth";
import { useCurrentAuthUser } from "../../auth";
import { useChromaVectorSyncRuntime } from "../runtime/useChromaVectorSyncRuntime";
import { formatChromaVectorSyncStatus } from "../service/chromaVectorSyncService";
import type { ChromaVectorSyncRunResult } from "../types";

/** 按执行结果状态选择提示块样式。 */
function getResultBoxStyle(status: ChromaVectorSyncRunResult["status"]) {
  if (status === "completed") {
    return {
      background: "var(--color-success-light-1)",
      borderColor: "var(--color-success-light-4)",
    };
  }
  return {
    background: "var(--color-warning-light-1)",
    borderColor: "var(--color-warning-light-4)",
  };
}

/** 同步向量库页面主容器样式。 */
const pageShellStyle = {
  display: "grid",
  gap: 16,
  padding: "4px 0 12px",
} as const;

/** 同步向量库页面说明区样式。 */
const headerPanelStyle = {
  display: "grid",
  gap: 12,
  padding: 16,
  border: "1px solid #e5e6eb",
  borderRadius: 8,
  background: "#fff",
} as const;

/** 同步向量库页面列表容器样式。 */
const tableWrapStyle = {
  overflowX: "auto",
  border: "1px solid #e5e6eb",
  borderRadius: 8,
  background: "#fff",
} as const;

/** 同步向量库页面表格样式。 */
const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  minWidth: 900,
} as const;

/** 同步向量库页面单元格样式。 */
const cellStyle = {
  borderBottom: "1px solid #e5e6eb",
  padding: "12px 14px",
  textAlign: "left",
  verticalAlign: "middle",
} as const;

/** 同步向量库页面表头单元格样式。 */
const headerCellStyle = {
  ...cellStyle,
  background: "#f7f8fa",
  color: "#4e5969",
  fontWeight: 600,
} as const;

/** 同步向量库页面主体。 */
export function ChromaVectorSyncFeaturePage() {
  const runtime = useChromaVectorSyncRuntime();
  const canExecute = canUsePermission(
    useCurrentAuthUser().data,
    "data.chroma_vector:execute",
  );
  const latestResultTarget = runtime.latestResult
    ? runtime.targets.find((target) => target.targetKey === runtime.latestResult?.targetKey)
    : undefined;

  return (
    <main style={pageShellStyle}>
      <section style={headerPanelStyle}>
        <div style={{ display: "grid", gap: 6 }}>
          <Typography.Title heading={5} style={{ marginBottom: 0 }}>
            同步向量库
          </Typography.Title>
          <Typography.Text type="secondary">
            通过 FlowerMaster 后端代理触发外部 Chroma embedding 服务，对专业、住宿点、
            考察点、学校和客户联系人执行全量重建。
          </Typography.Text>
        </div>
        <Space size={12} wrap>
          <Tag color="arcoblue">模式：全量重建</Tag>
          <Tag color="green">外部服务：后端 CHROMA_URL 配置</Tag>
          <Tag color="orange">前端不直连 localhost:8000</Tag>
        </Space>
      </section>

      {runtime.errorMessage ? (
        <section
          role="alert"
          style={{
            background: "var(--color-danger-light-1)",
            borderColor: "var(--color-danger-light-4)",
            borderStyle: "solid",
            borderWidth: 1,
            borderRadius: 6,
            padding: 12,
          }}
        >
          {runtime.errorMessage}
        </section>
      ) : null}

      <section style={{ display: "grid", gap: 10 }}>
        <Typography.Title heading={6} style={{ marginBottom: 0 }}>
          同步对象清单
        </Typography.Title>
        <Typography.Text type="secondary">
          每个对象单独触发，后端统一追加 rebuild=true 并等待外部 Chroma API 返回。
        </Typography.Text>
      </section>

      <div style={tableWrapStyle}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={headerCellStyle}>同步对象</th>
              <th style={headerCellStyle}>说明</th>
              <th style={headerCellStyle}>外部路径</th>
              <th style={headerCellStyle}>操作</th>
            </tr>
          </thead>
          <tbody>
            {runtime.loading ? (
              <tr>
                <td style={cellStyle} colSpan={4}>
                  正在加载同步对象...
                </td>
              </tr>
            ) : null}
            {!runtime.loading && runtime.targets.length === 0 ? (
              <tr>
                <td style={cellStyle} colSpan={4}>
                  暂无同步对象。
                </td>
              </tr>
            ) : null}
            {!runtime.loading
              ? runtime.targets.map((target) => {
                  const isExecuting = runtime.executingTargetKey === target.targetKey;
                  return (
                    <tr key={target.targetKey}>
                      <td style={cellStyle}>{target.targetName}</td>
                      <td style={cellStyle}>{target.description}</td>
                      <td style={cellStyle}>
                        <Typography.Text code>{target.externalPath}</Typography.Text>
                      </td>
                      <td style={cellStyle}>
                        <Button
                          type="primary"
                          loading={isExecuting}
                          disabled={!canExecute || !target.supportsRebuild}
                          onClick={() => void runtime.handleExecute(target.targetKey)}
                        >
                          {isExecuting ? "同步中" : "全量同步"}
                        </Button>
                      </td>
                    </tr>
                  );
                })
              : null}
          </tbody>
        </table>
      </div>

      {runtime.latestResult ? (
        <section
          role="status"
          style={{
            ...getResultBoxStyle(runtime.latestResult.status),
            borderStyle: "solid",
            borderWidth: 1,
            borderRadius: 6,
            padding: 12,
          }}
        >
          <div style={{ display: "grid", gap: 4 }}>
            <span>
              执行结果：
              {formatChromaVectorSyncStatus(runtime.latestResult.status)}
            </span>
            <span>
              同步对象：
              {latestResultTarget?.targetName ||
                runtime.latestResult.targetName ||
                runtime.latestResult.targetKey}
            </span>
            <span>{runtime.latestResult.message}</span>
            {runtime.latestResult.externalStatusCode ? (
              <span>外部状态码：{runtime.latestResult.externalStatusCode}</span>
            ) : null}
            {runtime.latestResult.externalUrl ? (
              <span>外部 URL：{runtime.latestResult.externalUrl}</span>
            ) : null}
          </div>
        </section>
      ) : null}
    </main>
  );
}
